import React, { useState } from 'react';
import { useAuth } from '../contexts/useAuth';

const DISMISS_KEY = 'verify_email_dismissed';

/**
 * Dismissible banner nudging signed-in users with an unconfirmed email to verify it.
 * Renders nothing when the user is confirmed, absent, or has dismissed it this session.
 */
const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!user || !user.email || user.emailConfirmed || dismissed) return null;

  const handleResend = async () => {
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const response = await fetch(`/api/auth/resend-confirmation?email=${encodeURIComponent(user.email)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to resend confirmation email');
      }
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to resend confirmation email');
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 dark:border-amber-500/20 dark:bg-amber-500/10">
      <div className="mx-auto flex max-w-(--breakpoint-2xl) flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          <span>
            Please verify your email — we sent a link to <strong>{user.email}</strong>.
          </span>
          {status === 'sent' && (
            <span className="text-xs text-green-700 dark:text-green-400">Confirmation email sent.</span>
          )}
          {status === 'error' && (
            <span className="text-xs text-red-600 dark:text-red-400">{errorMsg}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={status === 'sending' || status === 'sent'}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
          >
            {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Sent' : 'Resend email'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="rounded-lg px-2 py-1.5 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-500/20"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
